/**
 * ConfigurationSummary - Configuration 요약 표시 컴포넌트
 * 학습 순서와 주요 옵션을 간단히 요약하여 표시
 */
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { Settings, Play, ClipboardList, FileText, Edit2, Plus, Loader2, Eye, X } from 'lucide-react'
import { Dialog, DialogContent } from '@quebon/ui/components/dialog'
import type { LessonConfiguration, ComponentConfig, QuizLocation } from '@/types/configuration'
import { QUIZ_LOCATION_LABELS } from '@/types/configuration'
import { getDiaryQuizSets } from '@/api/diaryQuizSet'

interface ConfigurationSummaryProps {
  configuration: LessonConfiguration | null
  onEdit?: () => void  // 선택적 - 없으면 "자세히 보기"로 표시
  isLoading?: boolean
  isEditDisabled?: boolean        // 편집 비활성화 (Super Admin이 허용하지 않은 경우)
  editDisabledReason?: string     // 비활성화 사유 (툴팁)
}

// 컴포넌트 표시 이름 가져오기
const getComponentDisplayName = (component: ComponentConfig, t: (key: string) => string): string => {
  if (component.type === 'quiz' && component.location) {
    // QUIZ_LOCATION_LABELS에서 간결한 이름 추출 (예: "Pre-Quiz (사전 평가)" → "Pre Quiz")
    const fullLabel = QUIZ_LOCATION_LABELS[component.location as QuizLocation]
    return fullLabel?.split(' (')[0].replace('-', ' ') || 'Quiz'
  }
  const names: Record<string, string> = {
    video: t('summary.video'),
    assignment: t('summary.assignment'),
    reading: t('summary.reading'),
    interactive: t('summary.interactive'),
    discussion: t('summary.discussion'),
    download: t('summary.download')
  }
  return names[component.type] || component.type
}

// 컴포넌트 아이콘 가져오기
const getComponentIcon = (type: string) => {
  switch (type) {
    case 'video':
      return <Play className="w-3 h-3" />
    case 'quiz':
      return <ClipboardList className="w-3 h-3" />
    case 'assignment':
      return <FileText className="w-3 h-3" />
    default:
      return null
  }
}

// 비디오 옵션 요약
const getVideoOptionsSummary = (config: LessonConfiguration, t: (key: string, opts?: Record<string, unknown>) => string): string[] => {
  const videoComponent = config.sequence.find(c => c.type === 'video' && c.enabled)
  if (!videoComponent?.options) return []

  const summaries: string[] = []
  const opts = videoComponent.options

  if (opts.allowSkip) {
    summaries.push(t('summary.videoSkipAllow'))
  } else if (opts.allowSkipAfterFirstWatch) {
    summaries.push(t('summary.videoSkipAfterFirst'))
  } else {
    summaries.push(t('summary.videoSkipNotAllowed'))
  }

  if (opts.requiredWatchPercent) {
    summaries.push(t('summary.videoRequired', { percent: opts.requiredWatchPercent }))
  }

  if (opts.allowPlaybackSpeed) {
    summaries.push(t('summary.videoPlaybackSpeed'))
  }

  return summaries
}

// 퀴즈 옵션 요약
const getQuizOptionsSummary = (config: LessonConfiguration, t: (key: string, opts?: Record<string, unknown>) => string): string[] => {
  const quizComponents = config.sequence.filter(c => c.type === 'quiz' && c.enabled)
  if (quizComponents.length === 0) return []

  // Post Quiz 옵션 우선 사용
  const mainQuiz = quizComponents.find(q => q.location === 'post') || quizComponents[0]
  if (!mainQuiz?.options) return []

  const summaries: string[] = []
  const opts = mainQuiz.options

  if (opts.allowRetry) {
    if (opts.maxAttempts === 0) {
      summaries.push(t('summary.quizUnlimitedRetry'))
    } else if (opts.maxAttempts) {
      summaries.push(t('summary.quizRetryCount', { count: opts.maxAttempts }))
    }
  } else {
    summaries.push(t('summary.quizNoRetry'))
  }

  if (opts.passingScore) {
    summaries.push(t('summary.quizPassingScore', { score: opts.passingScore }))
  }

  if (opts.showAnswerImmediately) {
    summaries.push(t('summary.quizImmediateAnswer'))
  }

  return summaries
}

export function ConfigurationSummary({ configuration, onEdit, isLoading, isEditDisabled, editDisabledReason }: ConfigurationSummaryProps) {
  const { t } = useTranslation('configuration')
  const [showDetailModal, setShowDetailModal] = useState(false)

  // Diary Quiz Set 목록 조회 (assignment 컴포넌트에서 세트명 표시용)
  const assignmentComp = configuration?.sequence.find(c => c.type === 'assignment' && c.enabled)
  const { data: diaryQuizSets } = useQuery({
    queryKey: ['diary-quiz-sets'],
    queryFn: getDiaryQuizSets,
    enabled: !!assignmentComp?.options?.diaryQuizSetId,
  })

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 py-3 text-slate-500">
        <Loader2 className="w-4 h-4 animate-spin" />
        <span className="text-sm">{t('summary.loading')}</span>
      </div>
    )
  }

  // Configuration이 없는 경우
  if (!configuration) {
    return (
      <div>
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
            <Settings className="w-4 h-4 text-indigo-600" />
            {t('summary.title')}
          </h4>
          {onEdit && (
            <button
              onClick={onEdit}
              disabled={isEditDisabled}
              title={isEditDisabled ? editDisabledReason : undefined}
              className={`flex items-center gap-1 px-2 py-1 text-xs rounded-md transition-colors ${
                isEditDisabled
                  ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                  : 'bg-indigo-50 hover:bg-indigo-100 text-indigo-700'
              }`}
            >
              <Plus className="w-3 h-3" />
              {t('summary.addConfig')}
            </button>
          )}
        </div>
        <div className="p-3 bg-slate-50 rounded-lg">
          <p className="text-sm text-slate-500">{t('summary.noConfig')}</p>
        </div>
      </div>
    )
  }

  // 활성화된 컴포넌트만 필터링
  const enabledComponents = configuration.sequence.filter(c => c.enabled)
  const videoSummary = getVideoOptionsSummary(configuration, t)
  const quizSummary = getQuizOptionsSummary(configuration, t)

  // 버튼 핸들러 및 레이블 결정
  const canEdit = onEdit && !isEditDisabled
  const handleButtonClick = canEdit ? onEdit : () => setShowDetailModal(true)
  const buttonLabel = canEdit ? t('summary.edit') : t('summary.viewDetails')
  const ButtonIcon = canEdit ? Edit2 : Eye

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
          <Settings className="w-4 h-4 text-indigo-600" />
          {t('summary.title')}
        </h4>
        <button
          onClick={handleButtonClick}
          title={isEditDisabled ? editDisabledReason : undefined}
          className="flex items-center gap-1 px-2 py-1 text-xs bg-indigo-50 hover:bg-indigo-100 text-indigo-700 rounded-md transition-colors"
        >
          <ButtonIcon className="w-3 h-3" />
          {buttonLabel}
        </button>
      </div>

      {/* 자세히 보기 모달 */}
      {showDetailModal && (
        <ConfigurationDetailModal
          configuration={configuration}
          onClose={() => setShowDetailModal(false)}
        />
      )}

      <div className="p-3 bg-slate-50 rounded-lg space-y-3">
        {/* 상품 레벨 활동 */}
        {(configuration.productLearning?.startActivity?.enabled ||
          configuration.productLearning?.endActivity?.enabled) && (
          <div>
            <span className="text-xs font-medium text-slate-500">{t('summary.productLevel')}</span>
            <div className="flex flex-wrap gap-1 mt-1">
              {configuration.productLearning?.startActivity?.enabled && (
                <span className="px-2 py-0.5 bg-orange-50 text-orange-700 rounded text-xs">
                  {t('summary.startActivity', { types: (configuration.productLearning.startActivity.types || [])
                    .map(tp => tp === 'quiz' ? 'AIQ' : 'Video')
                    .join(', ') || '-' })}
                </span>
              )}
              {configuration.productLearning?.endActivity?.enabled && (
                <span className="px-2 py-0.5 bg-orange-50 text-orange-700 rounded text-xs">
                  {t('summary.endActivity', { types: (configuration.productLearning.endActivity.types || [])
                    .map(tp => tp === 'quiz' ? 'AIQ' : 'Video')
                    .join(', ') || '-' })}
                </span>
              )}
            </div>
          </div>
        )}

        {/* 학습 순서 */}
        <div>
          <span className="text-xs font-medium text-slate-500">{t('summary.learningOrder')}</span>
          <div className="flex items-center gap-1 mt-1 flex-wrap">
            {enabledComponents.map((component, index) => (
              <div key={`${component.type}-${component.location || index}`} className="flex items-center">
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${
                  component.required
                    ? 'bg-indigo-100 text-indigo-700'
                    : 'bg-slate-200 text-slate-600'
                }`}>
                  {getComponentIcon(component.type)}
                  {getComponentDisplayName(component, t)}
                </span>
                {index < enabledComponents.length - 1 && (
                  <span className="text-slate-400 mx-1">→</span>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* 비디오 옵션 */}
        {videoSummary.length > 0 && (
          <div>
            <span className="text-xs font-medium text-slate-500">{t('summary.video')}</span>
            <div className="flex flex-wrap gap-1 mt-1">
              {videoSummary.map((item, i) => (
                <span key={i} className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded text-xs">
                  {item}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* 퀴즈 옵션 */}
        {quizSummary.length > 0 && (
          <div>
            <span className="text-xs font-medium text-slate-500">{t('summary.quiz')}</span>
            <div className="flex flex-wrap gap-1 mt-1">
              {quizSummary.map((item, i) => (
                <span key={i} className="px-2 py-0.5 bg-amber-50 text-amber-700 rounded text-xs">
                  {item}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* 생각 정리하기 (assignment) 옵션 */}
        {assignmentComp?.options?.diaryQuizSetId && (
          <div>
            <span className="text-xs font-medium text-slate-500">{t('summary.assignment')}</span>
            <div className="flex flex-wrap gap-1 mt-1">
              <span className="px-2 py-0.5 bg-emerald-50 text-emerald-700 rounded text-xs">
                {diaryQuizSets?.find(s => s.id === assignmentComp.options!.diaryQuizSetId)?.name || t('summary.setNumber', { id: assignmentComp.options.diaryQuizSetId })}
              </span>
            </div>
          </div>
        )}

        {/* 레슨 간 순서 및 컴포넌트 순차 진행 */}
        <div className="pt-2 border-t border-slate-200 space-y-1">
          {/* 레슨 간 순서 */}
          <div className="flex items-center gap-2">
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs ${
              configuration.completionRules.lessonSequenceMode === 'flexible'
                ? 'bg-green-50 text-green-700'
                : 'bg-purple-50 text-purple-700'
            }`}>
              {t('summary.lessonLabel')} {configuration.completionRules.lessonSequenceMode === 'flexible' ? t('summary.lessonFlexible') : t('summary.lessonSequential')}
            </span>
            {configuration.completionRules.lessonSequenceOverridable !== false && (
              <span className="text-xs text-slate-400">{t('summary.overridableByChild')}</span>
            )}
          </div>
          {/* 컴포넌트 순차 진행 */}
          {configuration.completionRules.enforceSequentialProgress && (
            <span className="inline-flex items-center px-2 py-0.5 bg-blue-50 text-blue-700 rounded text-xs">
              {t('summary.componentSequential')}
            </span>
          )}
        </div>
      </div>
    </div>
  )
}

// 자세히 보기 모달 (읽기 전용)
interface ConfigurationDetailModalProps {
  configuration: LessonConfiguration
  onClose: () => void
}

function ConfigurationDetailModal({ configuration, onClose }: ConfigurationDetailModalProps) {
  const { t } = useTranslation('configuration')
  const enabledComponents = configuration.sequence.filter(c => c.enabled)

  // Diary Quiz Set 목록 조회 (assignment 세트명 표시용)
  const assignmentComponent = configuration.sequence.find(c => c.type === 'assignment' && c.enabled)
  const { data: diaryQuizSetsForModal } = useQuery({
    queryKey: ['diary-quiz-sets'],
    queryFn: getDiaryQuizSets,
    enabled: !!assignmentComponent?.options?.diaryQuizSetId,
  })

  return (
    <Dialog open={true} onOpenChange={(open) => { if (!open) onClose() }}>
    <DialogContent className="sm:max-w-lg p-0 gap-0">
      {/* Header */}
      <div className="flex items-center justify-between p-5 border-b border-slate-200 flex-shrink-0">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-indigo-100 rounded-lg">
            <Settings className="w-5 h-5 text-indigo-600" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-slate-900">{t('summary.detailTitle')}</h2>
            <p className="text-sm text-slate-500">{t('summary.detailDesc')}</p>
          </div>
        </div>
        <button
          onClick={onClose}
          className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto p-5 space-y-5">
        {/* 학습 순서 */}
        <div>
          <h4 className="text-sm font-semibold text-slate-700 mb-2">{t('summary.detailLearningOrder')}</h4>
          <div className="flex items-center gap-1 flex-wrap">
            {enabledComponents.map((component, index) => (
              <div key={`${component.type}-${component.location || index}`} className="flex items-center">
                <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-sm font-medium ${
                  component.required
                    ? 'bg-indigo-100 text-indigo-700'
                    : 'bg-slate-200 text-slate-600'
                }`}>
                  {getComponentIcon(component.type)}
                  {getComponentDisplayName(component, t)}
                  {component.required && <span className="text-xs">{t('summary.requiredTag')}</span>}
                </span>
                {index < enabledComponents.length - 1 && (
                  <span className="text-slate-400 mx-1">→</span>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* 비디오 설정 */}
        {(() => {
          const videoComp = configuration.sequence.find(c => c.type === 'video' && c.enabled)
          if (!videoComp?.options) return null
          return (
            <div>
              <h4 className="text-sm font-semibold text-slate-700 mb-2">{t('summary.detailVideoSettings')}</h4>
              <div className="bg-blue-50 rounded-lg p-3 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">{t('summary.skipAllow')}</span>
                  <span className="font-medium text-slate-900">
                    {videoComp.options.allowSkip ? t('summary.allowed') :
                     videoComp.options.allowSkipAfterFirstWatch ? t('summary.allowedAfterFirst') : t('summary.notAllowed')}
                  </span>
                </div>
                {videoComp.options.requiredWatchPercent && (
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">{t('summary.requiredWatchRate')}</span>
                    <span className="font-medium text-slate-900">{videoComp.options.requiredWatchPercent}%</span>
                  </div>
                )}
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">{t('summary.playbackSpeed')}</span>
                  <span className="font-medium text-slate-900">{videoComp.options.allowPlaybackSpeed ? t('summary.allowed') : t('summary.notAllowed')}</span>
                </div>
              </div>
            </div>
          )
        })()}

        {/* 퀴즈 설정 */}
        {(() => {
          const quizComp = configuration.sequence.find(c => c.type === 'quiz' && c.enabled && c.location === 'post')
            || configuration.sequence.find(c => c.type === 'quiz' && c.enabled)
          if (!quizComp?.options) return null
          return (
            <div>
              <h4 className="text-sm font-semibold text-slate-700 mb-2">{t('summary.detailQuizSettings')}</h4>
              <div className="bg-amber-50 rounded-lg p-3 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">{t('summary.retry')}</span>
                  <span className="font-medium text-slate-900">
                    {quizComp.options.allowRetry
                      ? (quizComp.options.maxAttempts === 0 ? t('summary.unlimited') : t('summary.retryCount', { count: quizComp.options.maxAttempts }))
                      : t('summary.notAllowed')}
                  </span>
                </div>
                {quizComp.options.passingScore && (
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">{t('summary.passingScore')}</span>
                    <span className="font-medium text-slate-900">{quizComp.options.passingScore}%</span>
                  </div>
                )}
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">{t('summary.immediateAnswerDisplay')}</span>
                  <span className="font-medium text-slate-900">{quizComp.options.showAnswerImmediately ? t('summary.yes') : t('summary.no')}</span>
                </div>
              </div>
            </div>
          )
        })()}

        {/* 생각 정리하기 설정 */}
        {(() => {
          const asgComp = configuration.sequence.find(c => c.type === 'assignment' && c.enabled)
          if (!asgComp?.options?.diaryQuizSetId) return null
          const setName = diaryQuizSetsForModal?.find(s => s.id === asgComp.options!.diaryQuizSetId)?.name
          return (
            <div>
              <h4 className="text-sm font-semibold text-slate-700 mb-2">{t('summary.detailAssignmentSettings')}</h4>
              <div className="bg-emerald-50 rounded-lg p-3 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">{t('summary.quizSet')}</span>
                  <span className="font-medium text-slate-900">{setName || t('summary.setNumber', { id: asgComp.options.diaryQuizSetId })}</span>
                </div>
              </div>
            </div>
          )
        })()}

        {/* 완료 규칙 */}
        <div>
          <h4 className="text-sm font-semibold text-slate-700 mb-2">{t('summary.completionRulesTitle')}</h4>
          <div className="bg-slate-50 rounded-lg p-3 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-slate-600">{t('summary.lessonOrder')}</span>
              <span className="font-medium text-slate-900">
                {configuration.completionRules.lessonSequenceMode === 'flexible' ? t('summary.lessonFlexible') : t('summary.lessonSequential')}
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-slate-600">{t('summary.componentSequentialProgress')}</span>
              <span className="font-medium text-slate-900">
                {configuration.completionRules.enforceSequentialProgress ? t('summary.yes') : t('summary.no')}
              </span>
            </div>
            {configuration.completionRules.minimumScore && (
              <div className="flex justify-between text-sm">
                <span className="text-slate-600">{t('summary.minimumPassingScore')}</span>
                <span className="font-medium text-slate-900">{configuration.completionRules.minimumScore}%</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="p-5 border-t border-slate-200 bg-slate-50 flex-shrink-0">
        <button
          onClick={onClose}
          className="w-full px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
        >
          {t('summary.close')}
        </button>
      </div>
    </DialogContent>
    </Dialog>
  )
}
